import { INVOICE_STATUSES } from "constants/invoicing";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { stringify } from "qs";
import { AccountsReceivableData } from "store/apis/financialMetrics";
import {
  AccountsCard,
  AccountsCardContent,
  TableRowData,
} from "./AccountsAnalysisCard";

type AccountsReceivableProps = {
  data?: AccountsReceivableData;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  forcedTab?: "customers" | "invoices";
  isExport?: boolean;
};

export const AccountsReceivable = ({
  data,
  isLoading,
  isSuccess,
  isError,
  forcedTab,
  isExport,
}: AccountsReceivableProps) => {
  const { link } = useConstructInternalLink();

  const customerRows: TableRowData[] =
    data?.top_customers.map((customer) => ({
      uuid: customer.uuid,
      label: customer.name,
      amount: customer.amount,
      link: link(`/books/revenue/customers/${customer.uuid}`),
    })) ?? [];

  const invoiceRows: TableRowData[] =
    data?.top_invoices.map((invoice) => ({
      uuid: invoice.uuid,
      label: invoice.invoice_number,
      amount: invoice.amount,
      link: link(`/books/invoices-and-customers/${invoice.uuid}`),
    })) ?? [];

  const pendingStatuses = [INVOICE_STATUSES.DUE, INVOICE_STATUSES.OVERDUE].join(
    ","
  );

  const invoicesViewAllUrl = `${link(
    "/books/invoices-and-customers"
  )}&${stringify({
    STATUS: pendingStatuses,
  })}`;

  return (
    <AccountsCard
      title="Accounts Receivable (AR)"
      loadingText="Loading AR Analysis..."
      emptyTitle="No outstanding Receivables"
      emptySubtitle="Create invoices to track Accounts Receivable"
      isLoading={isLoading}
      isSuccess={isSuccess}
      isError={isError}
      isEmpty={data?.total_outstanding === 0}
    >
      {data && (
        <>
          <AccountsCardContent
            defaultTab="customers"
            totalOutstanding={data.total_outstanding}
            forcedTab={forcedTab}
            isExport={isExport}
            tabs={[
              {
                value: "customers",
                label: "Customers",
                columnHeader: "Customers",
                rows: customerRows,
                viewAllUrl: link("/books/revenue/customers"),
                viewAllText: "View all Customers",
              },
              {
                value: "invoices",
                label: "Invoices",
                columnHeader: "Invoice",
                rows: invoiceRows,
                viewAllUrl: invoicesViewAllUrl,
                viewAllText: "View all pending invoices",
              },
            ]}
          />
        </>
      )}
    </AccountsCard>
  );
};
